/** @jsxImportSource preact */
import type { PaymentExtensionCreateEligibility } from '@plt/restate'
import { useState, useEffect } from 'preact/hooks'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { useAuth } from '../providers/auth'
import { DateTime } from 'luxon'

type PaymentExtension = {
  id: string
  externalId: string
  customerId: string
  paymentPlanId: string | null
  bookingReference: string
  source: string
  reason: string
  extensionDate: string
  timezone: string
  approvedExtensionDate: string | null
  decision: 'approved' | 'declined' | null
  decisionNote: string | null
  requestedAt: string
  finalisedAt: string | null
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

type FormAction = 'review' | 'create'

type PaymentExtensionsResponse = {
  extensions: PaymentExtension[]
  createEligibility: PaymentExtensionCreateEligibility
}

export function Payments() {
  const [bookingId, setBookingId] = useState('')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [extensions, setExtensions] = useState<PaymentExtension[]>([])
  const [selectedExtensionId, setSelectedExtensionId] = useState<string>('')
  const [decision, setDecision] = useState<'approved' | 'declined'>('approved')
  const [note, setNote] = useState('')
  const [extensionDate, setExtensionDate] = useState('')
  const [fetchingExtensions, setFetchingExtensions] = useState(false)
  const [selectedAction, setSelectedAction] = useState<FormAction>('review')
  const [createReason, setCreateReason] = useState('')
  const [createOtherReason, setCreateOtherReason] = useState('')
  const [showOtherReason, setShowOtherReason] = useState(false)
  const [createExtensionDate, setCreateExtensionDate] = useState('')
  const [createNote, setCreateNote] = useState('')
  const [createEligibility, setCreateEligibility] = useState<PaymentExtensionCreateEligibility | null>(null)

  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  const { user } = useAuth()

  // Effect to extract booking ID from Front context
  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  // Set default note prefill
  useEffect(() => {
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `Reviewed by ${username} in Front`
      setNote(notePrefill)
      setCreateNote(`Payment Extension created by ${username} in Front`)
    }
  }, [user])

  // Fetch payment extensions when bookingId changes
  useEffect(() => {
    if (bookingId) {
      fetchPaymentExtensions()
    } else {
      setExtensions([])
      setCreateEligibility(null)
      setSelectedAction('review')
    }
  }, [bookingId])

  // When an extension is selected, set the extension date to the requested date
  useEffect(() => {
    if (selectedExtensionId) {
      const selectedExtension = extensions.find(ext => ext.id === selectedExtensionId)
      if (selectedExtension) {
        const date = DateTime.fromISO(selectedExtension.extensionDate)
          .setZone(selectedExtension.timezone)
          .toFormat('yyyy-MM-dd')

        setExtensionDate(date)
      }
    }
  }, [selectedExtensionId, extensions])

  const fetchPaymentExtensions = async () => {
    if (!bookingId.trim()) {
      return
    }

    setFetchingExtensions(true)
    setErrorMessage('')
    setCreateEligibility(null)

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/bookings/${bookingId.trim()}/payment-extensions?includeCreateEligibility=true`,
        {
          credentials: 'include',
        },
      )

      if (!response.ok) {
        throw new Error('Failed to fetch payment extensions')
      }

      const data = (await response.json()) as PaymentExtensionsResponse
      setExtensions(data.extensions)
      setCreateEligibility(data.createEligibility)
      if (!data.createEligibility.allowed) {
        setSelectedAction('review')
      }
    } catch (error) {
      console.error('Error fetching payment extensions:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch payment extensions')
      setExtensions([])
      setCreateEligibility(null)
    } finally {
      setFetchingExtensions(false)
    }
  }

  const handleActionChange = (e: Event) => {
    const target = e.target as HTMLSelectElement
    setSelectedAction(target.value as FormAction)
    setFormStatus('idle')
    setErrorMessage('')
  }

  const resetForm = () => {
    setFormStatus('idle')
    setErrorMessage('')
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `Reviewed by ${username} in Front`
      setNote(notePrefill)
      setCreateNote(`Created by ${username} in Front`)
    } else {
      setNote('')
      setCreateNote('')
    }
    setDecision('approved')
    setExtensionDate('')
    setSelectedExtensionId('')
    setCreateReason('')
    setCreateOtherReason('')
    setShowOtherReason(false)
    setCreateExtensionDate('')
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      setCreateNote(`Created by ${username} in Front`)
    }
  }

  const handleReviewSubmit = async (e: Event) => {
    e.preventDefault()

    if (!bookingId.trim()) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    if (!selectedExtensionId) {
      setErrorMessage('Please select a payment extension')
      setFormStatus('error')
      return
    }

    if (!note) {
      setErrorMessage('Note is required')
      setFormStatus('error')
      return
    }

    if (!extensionDate) {
      setErrorMessage('Extension date is required')
      setFormStatus('error')
      return
    }

    setFormStatus('loading')
    setErrorMessage('')

    const selectedExtension = extensions.find(ext => ext.id === selectedExtensionId)

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/finalise-payment-extension`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          id: selectedExtension?.externalId,
          decision: decision,
          decisionNote: note,
          approvedExtensionDate: extensionDate,
          timezone: selectedExtension?.timezone,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to finalize payment extension')
      }

      setFormStatus('success')
      // Refresh the list of extensions
      fetchPaymentExtensions()
      // Reset the form
      resetForm()
    } catch (error) {
      console.error('Error finalizing payment extension:', error)
      setFormStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to finalize payment extension')
    }
  }

  const handleCreateSubmit = async (e: Event) => {
    e.preventDefault()

    if (!bookingId.trim()) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    if (!createReason) {
      setErrorMessage('Reason is required')
      setFormStatus('error')
      return
    }

    if (showOtherReason && !createOtherReason.trim()) {
      setErrorMessage('Please specify your reason')
      setFormStatus('error')
      return
    }

    if (!createExtensionDate) {
      setErrorMessage('Extension date is required')
      setFormStatus('error')
      return
    }

    if (!createNote.trim()) {
      setErrorMessage('Note is required')
      setFormStatus('error')
      return
    }

    setFormStatus('loading')
    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/payment-extensions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          reference: bookingId.trim(),
          reason: showOtherReason ? createOtherReason : createReason,
          extensionDate: createExtensionDate,
          note: createNote,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create payment extension')
      }

      setFormStatus('success')
      fetchPaymentExtensions()
      resetForm()
    } catch (error) {
      console.error('Error creating payment extension:', error)
      setFormStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create payment extension')
    }
  }

  const formatDate = (dateString: string, timezone: string) => {
    return DateTime.fromISO(dateString).setZone(timezone).toFormat('MMMM dd, yyyy hh:mm a ZZZZ')
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">Payments</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Payments</h1>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      <div className="mb-6">
        <label
          htmlFor="bookingId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Booking ID <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="bookingId"
            type="text"
            value={bookingId}
            onChange={e => setBookingId((e.target as HTMLInputElement).value)}
            className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
            placeholder="Enter Booking ID"
            required
          />
          {fetchingExtensions && (
            <div className="absolute right-3 top-2">
              <svg
                className="animate-spin h-5 w-5 text-indigo-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
        </div>
        {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
        {bookingId && isFrontApp && (
          <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
        )}
      </div>

      <div className="mt-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Payment Extensions</h2>

        {fetchingExtensions ? (
          <div className="p-4 bg-gray-50 rounded-md flex justify-center items-center">
            <svg
              className="animate-spin h-6 w-6 text-indigo-600 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Loading payment extensions...</span>
          </div>
        ) : (
          <>
            {extensions.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-md text-center">
                {bookingId
                  ? 'No payment extensions found for this booking.'
                  : 'Enter a booking ID to view payment extensions.'}
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Select
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Requested At
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Requested Extend To
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Reason
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {extensions.map(extension => (
                      <tr key={extension.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="radio"
                            name="extensionSelection"
                            value={extension.id}
                            checked={selectedExtensionId === extension.id}
                            onChange={() => setSelectedExtensionId(extension.id)}
                            disabled={extension.decision !== null}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {extension.id.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(extension.requestedAt, extension.timezone)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(extension.extensionDate, extension.timezone)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{extension.reason}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              extension.decision === null
                                ? 'bg-yellow-100 text-yellow-800'
                                : extension.decision === 'approved'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {extension.decision === null
                              ? 'Pending'
                              : extension.decision === 'approved'
                                ? 'Approved'
                                : 'Declined'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-6">
        <div className="mb-6">
          <label
            htmlFor="action"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Select Action
          </label>
          <select
            id="action"
            value={selectedAction}
            onChange={handleActionChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="review">Review Payment Extension</option>
            {createEligibility?.allowed && <option value="create">Create Payment Extension</option>}
          </select>
        </div>

        {createEligibility?.allowed === false && (
          <div
            role="status"
            className="mb-6 p-3 bg-yellow-50 text-yellow-800 rounded-md"
          >
            {createEligibility.reason}
          </div>
        )}

        {selectedAction === 'review' && extensions.length > 0 && (
          <form
            onSubmit={handleReviewSubmit}
            className="space-y-4"
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Review Payment Extension</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Decision <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="approved"
                    checked={decision === 'approved'}
                    onChange={() => setDecision('approved')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Approve</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    value="declined"
                    checked={decision === 'declined'}
                    onChange={() => setDecision('declined')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Decline</span>
                </label>
              </div>
            </div>

            <div>
              <label
                htmlFor="extensionDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Extension Date <span className="text-red-500">*</span>
              </label>
              <input
                id="extensionDate"
                type="date"
                value={extensionDate}
                onChange={e => setExtensionDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label
                htmlFor="note"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note <span className="text-red-500">*</span>
              </label>
              <textarea
                id="note"
                value={note}
                onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                required
              />
            </div>

            {formStatus === 'success' && (
              <div className="p-3 bg-green-50 text-green-700 rounded-md">
                Payment extension has been successfully reviewed.
              </div>
            )}

            {formStatus === 'error' && errorMessage && (
              <div className="p-3 bg-red-50 text-red-700 rounded-md">{errorMessage}</div>
            )}

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={resetForm}
                className="w-1/3 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={formStatus === 'loading' || !selectedExtensionId}
                className="w-2/3 px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? 'Processing...' : 'Submit Review'}
              </button>
            </div>
          </form>
        )}

        {selectedAction === 'create' && createEligibility?.allowed && (
          <form
            onSubmit={handleCreateSubmit}
            className="space-y-4"
          >
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Create Payment Extension</h2>

            <div>
              <label
                htmlFor="createReason"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reason for requesting an extension <span className="text-red-500">*</span>
              </label>
              <select
                id="createReason"
                value={createReason}
                onChange={e => {
                  const value = (e.target as HTMLSelectElement).value
                  setCreateReason(value)
                  setShowOtherReason(value === 'other')
                  if (value !== 'other') {
                    setCreateOtherReason('')
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option
                  value=""
                  disabled
                >
                  Select a reason
                </option>
                <option value="financial-difficulties">I'm having ongoing financial difficulties</option>
                <option value="unexpected-expense">
                  I had an unexpected expense or a delay in receiving money (salary, benefits, refund, etc.)
                </option>
                <option value="adjust-payment">
                  I'd like to adjust the payment to better match my income or cashflow
                </option>
                <option value="medical-family">
                  I have a medical or family situation affecting me or someone I care for
                </option>
                <option value="job-income">I lost my job or my income has been reduced</option>
                <option value="other">Other (please specify)</option>
              </select>
            </div>

            {showOtherReason && (
              <div>
                <label
                  htmlFor="createOtherReason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Please specify the reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="createOtherReason"
                  value={createOtherReason}
                  onChange={e => setCreateOtherReason((e.target as HTMLTextAreaElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                  placeholder="Please provide details about the situation..."
                  required
                />
              </div>
            )}

            <div>
              <label
                htmlFor="createExtensionDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Extension Date <span className="text-red-500">*</span>
              </label>
              <input
                id="createExtensionDate"
                type="date"
                value={createExtensionDate}
                onChange={e => setCreateExtensionDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label
                htmlFor="createNote"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Note <span className="text-red-500">*</span>
              </label>
              <textarea
                id="createNote"
                value={createNote}
                onChange={e => setCreateNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                required
              />
            </div>

            {formStatus === 'success' && (
              <div className="p-3 bg-green-50 text-green-700 rounded-md">
                Payment extension has been successfully created.
              </div>
            )}

            {formStatus === 'error' && errorMessage && (
              <div className="p-3 bg-red-50 text-red-700 rounded-md">{errorMessage}</div>
            )}

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={resetForm}
                className="w-1/3 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-2/3 px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? 'Processing...' : 'Create Extension'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
